// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { EmptyState } from '@/components/empty-state';
import { LocationName } from '@/components/location-name';
import { Customer } from '@/customer/api/types';
import { useLocations } from '@/location/hooks/use-locations';
import { pluralize } from '@/utilities/pluralize';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

interface Props {
  /**
   * The customer for which to display sites.
   */
  customer: Customer;
}

/**
 * A table component that renders the sites for each location assigned to the
 * customer. This component suspends while requesting locations.
 */
export function CustomerSiteTable(props: Props) {
  const { locations } = useLocations({
    filter: { customerId: props.customer.id },
  });

  if (locations.length === 0) {
    return (
      <EmptyState
        title="No locations"
        message="This customer does not have any locations"
      />
    );
  }

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Location</TableCell>
            <TableCell>Assets</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {locations.map((location) => (
            <TableRow key={location.id}>
              <TableCell>
                <LocationName location={location} variant="subtitle2" />
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {pluralize(location.assetCount, { singular: 'asset' })}
                </Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
